/**
 * Validação de arquivos de mídia (F4-T06, F4-T07, F4-T08, PLAN §39).
 *
 * Regra que orienta este arquivo: **nunca considerar sucesso só porque o `fetch()`
 * resolveu**. Um proxy de wi-fi de hotel devolve 200 com uma página HTML de login;
 * gravar isso como "vídeo baixado" é pior que falhar, porque o usuário só descobre
 * no avião.
 */

/** Tipos aceitos para vídeo local. Deliberadamente curto. */
export const ACCEPTED_VIDEO_MIME = ['video/mp4', 'video/quicktime', 'video/webm'] as const;

export const ACCEPTED_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.m4v', '.webm'] as const;

/** Arquivo menor que isto não é um vídeo — é uma página de erro ou um truncamento. */
const MIN_PLAUSIBLE_BYTES = 4 * 1024;

export interface ValidationIssue {
  code:
    | 'mime-rejected'
    | 'extension-rejected'
    | 'empty-file'
    | 'too-small'
    | 'size-mismatch'
    | 'checksum-mismatch'
    | 'not-decodable';
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  issues: ValidationIssue[];
}

function ok(): ValidationResult {
  return { valid: true, issues: [] };
}

function fail(...issues: ValidationIssue[]): ValidationResult {
  return { valid: false, issues };
}

export function isAcceptedMime(mimeType: string | undefined | null): boolean {
  if (!mimeType) return false;
  // Cabeçalhos costumam vir como "video/mp4; codecs=avc1".
  const base = mimeType.split(';')[0]?.trim().toLowerCase() ?? '';
  return (ACCEPTED_VIDEO_MIME as readonly string[]).includes(base);
}

export function hasAcceptedExtension(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return ACCEPTED_VIDEO_EXTENSIONS.some((extension) => lower.endsWith(extension));
}

/** SHA-256 em hexadecimal de um blob. */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface BlobValidationOptions {
  fileName?: string;
  expectedBytes?: number;
  expectedChecksum?: string;
  /** Tolerância no tamanho, em bytes. Servidores podem recomprimir levemente. */
  sizeToleranceBytes?: number;
}

/**
 * Valida um blob de vídeo.
 *
 * Ordem das checagens é do mais barato ao mais caro: tipo e tamanho antes do
 * checksum, que precisa ler o arquivo inteiro.
 */
export async function validateVideoBlob(
  blob: Blob,
  options: BlobValidationOptions = {},
): Promise<ValidationResult> {
  const { fileName, expectedBytes, expectedChecksum, sizeToleranceBytes = 0 } = options;
  const issues: ValidationIssue[] = [];

  if (blob.size === 0) {
    return fail({ code: 'empty-file', message: 'O arquivo está vazio.' });
  }

  if (blob.size < MIN_PLAUSIBLE_BYTES) {
    issues.push({
      code: 'too-small',
      message:
        `O arquivo tem apenas ${blob.size} bytes — pequeno demais para um vídeo. ` +
        'Costuma ser uma página de erro salva no lugar da mídia.',
    });
  }

  // O tipo pode vir do blob ou do nome do arquivo; basta um dos dois convencer.
  const mimeOk = isAcceptedMime(blob.type);
  const extensionOk = fileName ? hasAcceptedExtension(fileName) : false;

  if (!mimeOk && !extensionOk) {
    issues.push({
      code: blob.type ? 'mime-rejected' : 'extension-rejected',
      message: blob.type
        ? `Tipo "${blob.type}" não é um vídeo aceito (${ACCEPTED_VIDEO_MIME.join(', ')}).`
        : 'Não foi possível determinar o tipo do arquivo.',
    });
  }

  if (expectedBytes !== undefined && Math.abs(blob.size - expectedBytes) > sizeToleranceBytes) {
    issues.push({
      code: 'size-mismatch',
      message: `Esperado ${expectedBytes} bytes, recebido ${blob.size}.`,
    });
  }

  if (expectedChecksum) {
    const actual = await sha256Hex(blob);
    if (actual.toLowerCase() !== expectedChecksum.toLowerCase()) {
      issues.push({
        code: 'checksum-mismatch',
        message: 'O SHA-256 do arquivo não confere com o informado pela origem.',
      });
    }
  }

  return issues.length === 0 ? ok() : { valid: false, issues };
}

/**
 * Confirma que o navegador realmente consegue ler os metadados do vídeo (PLAN §39).
 *
 * É a única checagem que prova que o arquivo é reproduzível, e não apenas que tem o
 * tamanho e o tipo certos. Custa carregar o cabeçalho do arquivo, então roda depois
 * das validações baratas.
 */
export async function canDecodeVideo(blob: Blob, timeoutMs = 8000): Promise<boolean> {
  if (typeof document === 'undefined') return true;

  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;

  try {
    return await new Promise<boolean>((resolve) => {
      const finish = (result: boolean): void => {
        clearTimeout(timer);
        video.removeAttribute('src');
        video.load();
        resolve(result);
      };

      const timer = setTimeout(() => finish(false), timeoutMs);

      video.onloadedmetadata = () => finish(Number.isFinite(video.duration) && video.duration > 0);
      video.onerror = () => finish(false);
      video.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Valida uma resposta HTTP antes de gravar o corpo como mídia.
 *
 * Rejeita HTML explicitamente: é o formato de quase toda página de erro ou portal
 * cativo que se disfarça de download bem-sucedido.
 */
export function validateMediaResponse(response: Response): ValidationResult {
  if (!response.ok) {
    return fail({
      code: 'not-decodable',
      message: `A origem respondeu HTTP ${response.status}.`,
    });
  }

  const contentType = response.headers.get('content-type');
  if (contentType && /text\/html|application\/xhtml/i.test(contentType)) {
    return fail({
      code: 'mime-rejected',
      message:
        'A origem devolveu HTML em vez de vídeo — normalmente é uma página de erro ' +
        'ou um portal de login de wi-fi.',
    });
  }

  if (contentType && !isAcceptedMime(contentType)) {
    return fail({
      code: 'mime-rejected',
      message: `Content-Type "${contentType}" não é um vídeo aceito.`,
    });
  }

  return ok();
}
